import React, { useState, useEffect, useCallback } from "react";
import { Table, Card, Row, Col, Spinner, Alert, Badge, Form, Button } from "react-bootstrap";
import { FaBalanceScale, FaFileExcel, FaSync } from "react-icons/fa";
import * as XLSX from "xlsx";
import firebaseApp from "../../../firebase/credenciales";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { periodoActual, toNum } from "../utils";

const db = getFirestore(firebaseApp);

const BalanceComprobacion = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mayorData, setMayorData] = useState([]);

  const cargarBalance = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      setError("");
      
      const q = query(
        collection(db, "gco_asientos"),
        where("ruc_empresa", "==", rucEmpresa)
      );
      const snap = await getDocs(q);
      
      const mayor = {};
      snap.docs.forEach(doc => {
        const a = doc.data();
        if (periodo && !String(a.fecha).startsWith(periodo)) return;

        (a.debe || []).forEach(l => {
          const cuenta = l.cuenta;
          if (!cuenta) return;
          if (!mayor[cuenta]) {
            mayor[cuenta] = { cuenta, descripcion: l.descripcion, debe: 0, haber: 0 };
          }
          mayor[cuenta].debe += toNum(l.monto);
        });

        (a.haber || []).forEach(l => {
          const cuenta = l.cuenta;
          if (!cuenta) return;
          if (!mayor[cuenta]) {
            mayor[cuenta] = { cuenta, descripcion: l.descripcion, debe: 0, haber: 0 };
          }
          mayor[cuenta].haber += toNum(l.monto);
        });
      });

      const list = Object.values(mayor).map(item => {
        const d = Number(item.debe.toFixed(2));
        const h = Number(item.haber.toFixed(2));
        const diff = d - h;
        
        return {
          cuenta: item.cuenta,
          descripcion: item.descripcion,
          debe: d,
          haber: h,
          deudor: diff > 0 ? Number(diff.toFixed(2)) : 0,
          acreedor: diff < 0 ? Number(Math.abs(diff).toFixed(2)) : 0
        };
      }).sort((a, b) => a.cuenta.localeCompare(b.cuenta));

      setMayorData(list);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar el Balance de Comprobación.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa, periodo]);

  useEffect(() => {
    cargarBalance();
  }, [cargarBalance]);

  const totalDebe = Number(mayorData.reduce((sum, item) => sum + item.debe, 0).toFixed(2));
  const totalHaber = Number(mayorData.reduce((sum, item) => sum + item.haber, 0).toFixed(2));
  const totalDeudor = Number(mayorData.reduce((sum, item) => sum + item.deudor, 0).toFixed(2));
  const totalAcreedor = Number(mayorData.reduce((sum, item) => sum + item.acreedor, 0).toFixed(2));
  
  const diferencia = Number(Math.abs(totalDebe - totalHaber).toFixed(2));
  const deudorAcreedorDiferencia = Number(Math.abs(totalDeudor - totalAcreedor).toFixed(2));
  const balanceCuadrado = diferencia === 0 && deudorAcreedorDiferencia === 0;

  const exportarExcel = () => {
    const rows = mayorData.map(item => ({
      Cuenta: item.cuenta,
      Descripción: item.descripcion,
      Debe: item.debe.toFixed(2),
      Haber: item.haber.toFixed(2),
      "Saldo Deudor": item.deudor.toFixed(2),
      "Saldo Acreedor": item.acreedor.toFixed(2)
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Balance_Comprobacion");
    XLSX.writeFile(wb, `Balance_Comprobacion_${rucEmpresa}_${periodo}.xlsx`);
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center g-3">
            <Col md={1} className="text-center">
              <div className="rounded-3 p-3 d-inline-block bg-light text-secondary">
                <FaBalanceScale size={24} />
              </div>
            </Col>
            <Col md={5}>
              <h5 className="mb-1 fw-semibold">Balance de Comprobación</h5>
              <p className="text-muted mb-0 small">Suma de cargos, abonos y saldos de todas las cuentas.</p>
            </Col>
            <Col md={3}>
              <Form.Control type="month" className="fw-bold" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
            </Col>
            <Col md={3} className="text-end d-flex gap-2 justify-content-end">
              <Button variant="outline-success" className="rounded-pill px-3 btn-sm fw-bold" onClick={exportarExcel} disabled={loading}><FaFileExcel /> Excel</Button>
              <Button variant="light" className="rounded-pill btn-sm" onClick={cargarBalance} disabled={loading}><FaSync /></Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alerta de Cuadre Contable */}
      <div className="mb-4">
        {balanceCuadrado ? (
          <Alert variant="success" className="d-flex align-items-center justify-content-between border-0 shadow-sm py-3 px-4" style={{ borderRadius: "12px" }}>
            <div className="fw-bold">✓ BALANCE CUADRADO</div>
            <div className="small opacity-75">Las sumas del Debe / Haber y Saldos Deudor / Acreedor concilian al 100%.</div>
          </Alert>
        ) : (
          <Alert variant="danger" className="d-flex align-items-center justify-content-between border-0 shadow-sm py-3 px-4" style={{ borderRadius: "12px" }}>
            <div className="fw-bold">🔴 DIFERENCIA DETECTADA: S/ {diferencia.toFixed(2)}</div>
            <div className="small opacity-75">Existe un descuadre en los asientos contables del período. Revise las cuentas involucradas.</div>
          </Alert>
        )}
      </div>

      {/* Grid del Balance */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /><p className="mt-3 text-muted">Generando balance de comprobación desde gco_asientos...</p></div>
          ) : mayorData.length === 0 ? (
            <div className="text-center py-5">
              <FaBalanceScale size="4rem" className="text-muted opacity-25 mb-3" />
              <h5 className="text-muted fw-semibold">No hay movimientos registrados para este período contable</h5>
            </div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">Código</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Cuenta Contable</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Debe</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Haber</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Saldo Deudor</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Saldo Acreedor</th>
                </tr>
              </thead>
              <tbody>
                {mayorData.map((item) => (
                  <tr key={item.cuenta}>
                    <td className="px-4 py-3 fw-bold text-dark">{item.cuenta}</td>
                    <td className="px-4 py-3 text-muted">{item.descripcion || "Cuenta Contable"}</td>
                    <td className="px-4 py-3 text-end fw-semibold text-success">{item.debe > 0 ? item.debe.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 text-end fw-semibold text-danger">{item.haber > 0 ? item.haber.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 text-end fw-bold text-success">{item.deudor > 0 ? item.deudor.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 text-end fw-bold text-danger">{item.acreedor > 0 ? item.acreedor.toFixed(2) : "—"}</td>
                  </tr>
                ))}
                {/* Fila de Totales */}
                <tr style={{ backgroundColor: "#f8f9fa", borderTop: "2px solid #dee2e6" }}>
                  <td colSpan={2} className="px-4 py-3 fw-bold text-dark text-uppercase">Totales del Periodo</td>
                  <td className="px-4 py-3 text-end fw-bold text-success">S/ {totalDebe.toFixed(2)}</td>
                  <td className="px-4 py-3 text-end fw-bold text-danger">S/ {totalHaber.toFixed(2)}</td>
                  <td className="px-4 py-3 text-end fw-bold text-success">S/ {totalDeudor.toFixed(2)}</td>
                  <td className="px-4 py-3 text-end fw-bold text-danger">S/ {totalAcreedor.toFixed(2)}</td>
                </tr>
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BalanceComprobacion;
